import * as THREE from 'three';
import type { SceneManager } from '../scene/SceneManager';
import type { Pergola } from '../pergola/Pergola';
import type { Dimensions } from '../pergola/dimensions';

export interface CapturedViews {
  front: string;
  side: string;
  plan: string;
  iso: string;
}

const CAP_W = 1100;
const CAP_H = 825; // 4:3
/** Fondo neutro de las vistas (igual que las celdas de la ficha). */
export const CAPTURE_BG_HEX = '#eef1f4';
const CAPTURE_BG = new THREE.Color(CAPTURE_BG_HEX);

/**
 * Renderiza 4 vistas (frontal, lateral, planta cenital, isométrica) con las cotas
 * visibles y devuelve sus PNG en dataURL. Usa cámaras ortográficas para las vistas
 * técnicas y perspectiva para la isométrica. No interfiere con la vista en vivo:
 * captura síncrona (render + toDataURL en el mismo turno) y restaura el tamaño.
 */
export function captureViews(sm: SceneManager, pergola: Pergola, dimensions: Dimensions): CapturedViews {
  const renderer = sm.renderer;
  const scene = sm.scene;

  const prevSize = renderer.getSize(new THREE.Vector2());
  const prevPixelRatio = renderer.getPixelRatio();
  const prevDimsVisible = dimensions.visible;
  const prevBg = scene.background;

  dimensions.setVisible(true);
  scene.background = CAPTURE_BG; // fondo claro neutro para la ficha técnica
  renderer.setPixelRatio(1);
  renderer.setSize(CAP_W, CAP_H, false);

  // Bounding box de la pérgola + cotas para encuadrar.
  const box = new THREE.Box3().setFromObject(pergola.group);
  box.expandByObject(dimensions.group);
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  const maxDim = Math.max(size.x, size.y, size.z);

  const shot = (cam: THREE.Camera): string => {
    renderer.render(scene, cam);
    return renderer.domElement.toDataURL('image/png');
  };

  const front = shot(orthoCam(size.x, size.y, center, new THREE.Vector3(0, 0, 1), new THREE.Vector3(0, 1, 0), maxDim));
  const side = shot(orthoCam(size.z, size.y, center, new THREE.Vector3(1, 0, 0), new THREE.Vector3(0, 1, 0), maxDim));
  const plan = shot(orthoCam(size.x, size.z, center, new THREE.Vector3(0, 1, 0), new THREE.Vector3(0, 0, -1), maxDim));
  const iso = shot(isoCam(center, maxDim));

  // Restaurar estado de la vista en vivo.
  scene.background = prevBg;
  dimensions.setVisible(prevDimsVisible);
  renderer.setPixelRatio(prevPixelRatio);
  renderer.setSize(prevSize.x, prevSize.y, false);
  sm.resize();

  return { front, side, plan, iso };
}

/** Cámara ortográfica encuadrando un contenido (contentW × contentH) desde `dir`. */
function orthoCam(
  contentW: number,
  contentH: number,
  center: THREE.Vector3,
  dir: THREE.Vector3,
  up: THREE.Vector3,
  maxDim: number,
): THREE.OrthographicCamera {
  const margin = 1.12;
  let halfW = (contentW / 2) * margin;
  let halfH = (contentH / 2) * margin;
  const aspect = CAP_W / CAP_H;
  if (halfW / halfH < aspect) halfW = halfH * aspect;
  else halfH = halfW / aspect;

  const cam = new THREE.OrthographicCamera(-halfW, halfW, halfH, -halfH, 0.01, maxDim * 10 + 100);
  cam.position.copy(center.clone().add(dir.clone().normalize().multiplyScalar(maxDim * 4 + 10)));
  cam.up.copy(up);
  cam.lookAt(center);
  cam.updateProjectionMatrix();
  return cam;
}

/** Cámara en perspectiva para la vista isométrica. */
function isoCam(center: THREE.Vector3, maxDim: number): THREE.PerspectiveCamera {
  const cam = new THREE.PerspectiveCamera(42, CAP_W / CAP_H, 0.1, 1000);
  const dir = new THREE.Vector3(1, 0.78, 1).normalize();
  cam.position.copy(center.clone().add(dir.multiplyScalar(maxDim * 2.3 + 2)));
  cam.lookAt(center);
  cam.updateProjectionMatrix();
  return cam;
}
